import React, { useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

export default function Choice ({ data, currentQuestion, responses, setResponses, choicesSelected, setChoicesSelected, setText, text, embedded, embeddedPanelHeight }) {
	const { t } = useTranslation('survey');

	let min = 0;
	const questionEl = document.querySelector('.question__question');
	// Set the min, this is either the setting from the survey...
	if (data[currentQuestion].min_answers) {
		min = data[currentQuestion].min_answers;
	} else if (data[currentQuestion].required) {
		// ...or if the survey has choices as required, and min is set to 0 then re-set it to 1
		if (min < 1) {
			min = 1;
		}
	}
	let max = data[currentQuestion].max_answers;
	let showOther = data[currentQuestion].other;

	// Tweak error messages, this varies with the min and max.
	let errorMessage = false;
	if (min || max > 1) {
		if (min && max) {
			if (min === max) {
				errorMessage = t('survey.choice.choose-answer', { min: min });
				if (min > 1) {
					errorMessage = t('survey.choice.choose-answers', { min: min });
				}
			} else {
				errorMessage = t('survey.choice.choose-between', { min: min, max: max });
			}
		} else if (min) {
			errorMessage = t('survey.choice.choose-least', { min: min });
		} else {
			errorMessage = t('survey.choice.choose-upto', { max: max });
		}
	}
	const errorSelector = 'q' + [currentQuestion + 1] + 'error';

	const [ansValue, setansValue] = useState(false);
	let answers = [];

	const handleChoiceChange = (evt) => {
		const { value, checked } = evt.target;
		const { choices } = choicesSelected;
		if (errorMessage) {
			// Move the p back into the wrapper by default
			document.querySelector('#' + errorSelector).parentNode.append(document.querySelector('#' + errorSelector + '-content'));
		}

		// It's the other option show the extra textbox, otherwise hide it.
		if (value === 'other' && showOther) {
			if (checked) {
				document.querySelector('.question__other').setAttribute('aria-hidden', 'false');
			} else {
				document.querySelector('.question__other').setAttribute('aria-hidden', 'true');
			}

			// Check size on question change
			embeddedPanelHeight('on other');
		}

		if (max === 1) {
			// On radios the current option is always the only one selected
			// Move the p back into the wrapper
			if (errorMessage) {
				document.querySelector('#' + errorSelector).parentNode.append(document.querySelector('#' + errorSelector + '-content'));
			}
			setChoicesSelected({
				choices: [value],
			});
		} else {
			// On checkboxes...
			if (checked) {
				// If it's greater than the max don't check
				// choices.length is 0 indexed
				if (max && (choices.length + 1) > max) {
					// Move the p into the error message
					if (errorMessage) {
						document.querySelector('#' + errorSelector).append(document.querySelector('#' + errorSelector + '-content'));
					}
					document.querySelector('.question__error').scrollIntoView({
						behavior: 'smooth',
					});
					return false;
				}

				// ...add to the array if selecting a new one
				setChoicesSelected({
					choices: [...choices, value],
				});
			} else {
				// Move the p back into the wrapper
				if (errorMessage) {
					document.querySelector('#' + errorSelector).parentNode.append(document.querySelector('#' + errorSelector + '-content'));
				}
				// ...or remove from the array if unchecking
				setChoicesSelected({
					choices: choices.filter(
						(e) => e !== value
					),
				});
			}
		}
	};

	const otherHandler = (e) => {
		setText(e.target.value);
	};

	useEffect(() => {
		// Work out if there are any saved options
		if (responses[currentQuestion]) {
			if (Object.values(responses[currentQuestion]) == '') {
				// If there's nothing saved set as a blank array
				setChoicesSelected({
					choices: [],
				});
			} else {
				// Otherwise set to the array saved in responses
				setChoicesSelected({
					choices: Object.values(responses[currentQuestion])[0],
				});
			}
		}
	}, [responses[currentQuestion]]);

	useEffect(() => {
		if (showOther) {
			let otherKey = false;
			for (const response in responses) {
				for (const responseObj in responses[response]) {
					if (responseObj === (data[currentQuestion].title + ' - other')) {
						otherKey = response;
					}
				}
			}

			if (otherKey) {
				if (showOther && !(Object.values(responses[otherKey])[0] === '')) {
					setText(Object.values(responses[otherKey])[0]);
				}
			}
		}
	}, [responses]);

	return (
		<>
			<main className="survey__panel question--checkbox">
				<p className="question__number">{t('survey.generic.Step')} {[currentQuestion + 1]}/{data.length}</p>
				<fieldset>
					<legend>{data[currentQuestion].title}</legend>
					<div className="question__question">
						<h1 className='question__label' key={[currentQuestion + 1]}>
							{data[currentQuestion].title}&nbsp;{(min >= 1) ? <span className="question__required">({t('survey.generic.Required')})</span> : ''}
						</h1>
						<div className="question__instructions">
							<div className="markdown">
								<Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{data[currentQuestion].instruction}</Markdown>
							</div>
						</div>
						<div className={`${
							data[currentQuestion].answers.some(answers => answers.image_url !== null) ? 'question__input question__input--toggle toggle--image' : 'question__input question__input--toggle'
						}`}>
							<span className="min-max">{errorMessage}</span>
							{
								data[currentQuestion].answers.map((answers, index) => {
									return (
										<div key={[currentQuestion + 1] + answers.label} className="toggle">
											<input key={answers.label}
												type={max === 1 ? 'radio' : 'checkbox'}
												value={answers.value}
												id={'question' + [currentQuestion + 1] + '-' + index}
												name={'question' + [currentQuestion + 1]}
												className="toggle__input"
												onChange={(evt) => { handleChoiceChange(evt); }}
												checked={choicesSelected.choices.some((x) => x === answers.value) ? 'checked' : ''}
											/>
											<label
												htmlFor={'question' + [currentQuestion + 1] + '-' + index}
												className={`toggle__label ${choicesSelected.choices.some((x) => x === answers.value) ? 'checked-option' : ''}`}
											>
												<span>{answers.label}</span>
												{answers.image_url !== null ? <div><img src={answers.image_url} width="200" height="200" alt="" /></div> : null}
											</label>
										</div>
									);
								})
							}
							{(showOther) ? <div key={[currentQuestion + 1] + 'other'} className="toggle">
								<input key='Other'
									type={max === 1 ? 'radio' : 'checkbox'}
									value='other'
									id={'question' + [currentQuestion + 1] + '-other'}
									name={'question' + [currentQuestion + 1]}
									className="toggle__input"
									onChange={(evt) => { handleChoiceChange(evt); }}
									checked={choicesSelected.choices.some((x) => x === 'other') ? 'checked' : ''}
								/>
								<label
									htmlFor={'question' + [currentQuestion + 1] + '-other'}
									className={`toggle__label ${choicesSelected.choices.some((x) => x === 'other') ? 'checked-option' : ''}`}
								>
									<span>{t('survey.generic.Other')}</span>
								</label>
							</div> : ''}
							{(showOther) ? <div className="question__other" aria-hidden={choicesSelected.choices.some((x) => x === 'other') ? 'false' : 'true'}>
								<textarea
									placeholder={t('survey.phrases.please-specify') + " '" + t('survey.generic.Other') + "'"}
									id={'question' + [currentQuestion + 1] + '-other-text'}
									name={'question' + [currentQuestion + 1] + '-other'}
									onChange={(e) => otherHandler(e)}
									value={text}
								/>
							</div> : ''}
						</div>
						{(errorMessage) ? <div className="question__error-wrapper"><div id={'q' + [currentQuestion + 1] + 'error'} className="question__error" role="alert" aria-atomic="true"></div><p id={'q' + [currentQuestion + 1] + 'error-content'} className="question__error-content">{errorMessage}</p></div> : ''}
					</div>
				</fieldset>
			</main>
		</>
	);
}
